import { Card, CardContent } from "@/components/ui/card";

/**
 * MobileCardView - Renders data as touch-friendly cards for mobile displays
 * 
 * A reusable component that transforms tabular data into a card-based layout
 * optimized for mobile screens. Use this instead of tables on small viewports.
 * 
 * @example
 * ```tsx
 * <MobileCardView
 *   data={items}
 *   renderCard={(item) => <div>{item.name}</div>}
 *   emptyState={<EmptyState title="No items" />}
 * />
 * ```
 */
interface MobileCardViewProps<T> {
  /** Array of data items to render as cards */
  data: T[];
  /** Render function for each card's content */
  renderCard: (item: T, index: number) => React.ReactNode;
  /** Optional component to show when data array is empty */
  emptyState?: React.ReactNode;
}

export function MobileCardView<T extends { id: string }>({ 
  data, 
  renderCard,
  emptyState 
}: MobileCardViewProps<T>) {
  if (data.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <Card key={item.id} className="touch-manipulation">
          <CardContent className="p-4">
            {renderCard(item, index)}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
